/**
 * useDeadlineCalendarExport
 *
 * Thin orchestrator hook for the calendar export modal. PR-3 of the
 * Watchtower Calendar Export feature; see
 * docs/architecture/watchtower-calendar-export.md §6.
 *
 * The submit pipeline (audit → generate → download) lives inside the
 * `DeadlineCalendarExportModal` component itself; this hook only:
 *  - holds open/closed state and the deadline + recipientName context,
 *  - returns a bound `<Modal />` component the caller renders in its tree,
 *  - exposes `open(deadline, ctx)` / `close()` for triggers in cards/details.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  DeadlineCalendarExportModal,
  type DeadlineCalendarExportModalProps,
  type DeadlineRef,
} from '../components/deadlines/DeadlineCalendarExportModal';
import type { Deadline } from '../components/deadlines/types';

/**
 * Either a fully-loaded `Deadline` (e.g. from `DeadlineDetail`, which has
 * already fetched the document) OR a `DeadlineRef` (e.g. from a list-view
 * card, which only has a `DeadlineSummary`). When a ref is passed, the
 * modal fetches the full deadline itself and surfaces any failure through
 * its own inline error banner — same UX as audit/generator/download
 * failures. (TASK-016 Codex remediation, finding 2.)
 */
export type DeadlineOpenTarget = Deadline | DeadlineRef;

export interface UseDeadlineCalendarExportResult {
  /**
   * Open the modal for a specific deadline + recipient.
   *
   * Pass a full `Deadline` if you already have one loaded; pass a
   * `DeadlineRef` (`{ caseFileId, deadlineId }`) to have the modal fetch
   * it. `recipientName` is optional — when omitted/empty the PHI toggle is
   * disabled and the audit payload's `includedCategories.recipientName` is
   * forced to `false`. (TASK-016 Codex remediation, finding 1.)
   */
  open: (
    target: DeadlineOpenTarget,
    ctx: { recipientName?: string },
  ) => void;
  /** Close the modal without exporting. */
  close: () => void;
  /** Read-only state — useful for tests + parent UX. */
  state: {
    isOpen: boolean;
    deadline: Deadline | null;
    deadlineRef: DeadlineRef | null;
    recipientName: string | undefined;
  };
  /**
   * Bound modal component. Caller renders `<Modal />` once somewhere in its
   * tree; the hook supplies the props from internal state.
   */
  Modal: React.FC;
}

function isFullDeadline(target: DeadlineOpenTarget): target is Deadline {
  // `userId` is required on `Deadline` but absent from `DeadlineRef`. This
  // is a structural narrow rather than a tag check, but the field set is
  // stable and tested via the `DeadlineSummary`/`Deadline` interfaces.
  return 'userId' in target;
}

export function useDeadlineCalendarExport(options?: {
  onExported?: DeadlineCalendarExportModalProps['onExported'];
}): UseDeadlineCalendarExportResult {
  const [isOpen, setIsOpen] = useState(false);
  const [deadline, setDeadline] = useState<Deadline | null>(null);
  const [deadlineRef, setDeadlineRef] = useState<DeadlineRef | null>(null);
  const [recipientName, setRecipientName] = useState<string | undefined>(undefined);

  const open = useCallback(
    (target: DeadlineOpenTarget, ctx: { recipientName?: string }) => {
      if (isFullDeadline(target)) {
        setDeadline(target);
        setDeadlineRef(null);
      } else {
        setDeadline(null);
        setDeadlineRef({
          caseFileId: target.caseFileId,
          deadlineId: target.deadlineId,
        });
      }
      setRecipientName(ctx.recipientName);
      setIsOpen(true);
    },
    [],
  );

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onExported = options?.onExported;

  const Modal = useMemo<React.FC>(() => {
    const Bound: React.FC = () => {
      // No target chosen yet — render nothing.
      if (!deadline && !deadlineRef) return null;
      return (
        <DeadlineCalendarExportModal
          isOpen={isOpen}
          onClose={close}
          deadline={deadline ?? undefined}
          deadlineRef={deadlineRef ?? undefined}
          recipientName={recipientName}
          onExported={(result) => {
            onExported?.(result);
          }}
        />
      );
    };
    Bound.displayName = 'BoundDeadlineCalendarExportModal';
    return Bound;
  }, [isOpen, deadline, deadlineRef, recipientName, close, onExported]);

  return {
    open,
    close,
    state: { isOpen, deadline, deadlineRef, recipientName },
    Modal,
  };
}

export default useDeadlineCalendarExport;
